import { drainOutbox, drainUntilEmpty, type SyncOutcome } from "./drain";
import { countPending } from "../local/repositories";

/**
 * When to sync.
 *
 * Three triggers, and the interval is chosen against a quota rather than a
 * feeling: 30 seconds across three terminals over a 12-hour day is 4,320
 * requests, about 4% of the free plan's 100,000/day (research.md R5).
 *
 * Nothing here is on the sale path. A sale is committed and confirmed locally
 * before the scheduler ever sees it.
 */

export const SYNC_INTERVAL_MS = 30_000;

export interface SyncStatus {
  online: boolean;
  syncing: boolean;
  pending: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

type Listener = (status: SyncStatus) => void;

export class SyncScheduler {
  #timer: ReturnType<typeof setInterval> | null = null;
  #listeners = new Set<Listener>();
  #status: SyncStatus = {
    online: typeof navigator === "undefined" ? true : navigator.onLine,
    syncing: false,
    pending: 0,
    lastSyncAt: null,
    lastError: null,
  };

  get status(): SyncStatus {
    return this.#status;
  }

  subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.#status);
    return () => this.#listeners.delete(listener);
  }

  start(): void {
    if (this.#timer) return;

    window.addEventListener("online", this.#onOnline);
    window.addEventListener("offline", this.#onOffline);
    document.addEventListener("visibilitychange", this.#onVisible);

    this.#timer = setInterval(() => void this.syncNow(), SYNC_INTERVAL_MS);
    void this.syncNow();
  }

  stop(): void {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
    window.removeEventListener("online", this.#onOnline);
    window.removeEventListener("offline", this.#onOffline);
    document.removeEventListener("visibilitychange", this.#onVisible);
  }

  async syncNow(drainAll = false): Promise<SyncOutcome> {
    if (this.#status.syncing) {
      return { attempted: 0, applied: 0, duplicate: 0, rejected: 0, pending: this.#status.pending };
    }

    this.#update({ syncing: true });
    try {
      const outcome = drainAll ? await drainUntilEmpty() : await drainOutbox();
      this.#update({
        syncing: false,
        pending: outcome.pending,
        lastError: outcome.error ?? null,
        ...(outcome.error ? {} : { lastSyncAt: new Date().toISOString() }),
      });
      return outcome;
    } catch (error) {
      this.#update({
        syncing: false,
        lastError: error instanceof Error ? error.message : "Sync failed",
        pending: await countPending(),
      });
      throw error;
    }
  }

  async refreshPending(): Promise<void> {
    this.#update({ pending: await countPending() });
  }

  /**
   * On reconnect, drain everything rather than one batch.
   *
   * After a long outage a single 25-envelope batch would leave hundreds behind
   * and take an hour of 30-second ticks to clear — during which the back office
   * shows figures that are quietly wrong.
   */
  #onOnline = () => {
    this.#update({ online: true });
    void this.syncNow(true);
  };

  #onOffline = () => {
    this.#update({ online: false });
  };

  #onVisible = () => {
    // Staff picking the tablet back up is a good moment to reconcile.
    if (document.visibilityState === "visible" && navigator.onLine) {
      void this.syncNow();
    }
  };

  #update(patch: Partial<SyncStatus>): void {
    this.#status = { ...this.#status, ...patch };
    for (const listener of this.#listeners) listener(this.#status);
  }
}

export const syncScheduler = new SyncScheduler();
